import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Radio } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import { EMPTY_ID } from '@totem/components/common/referencesModal/ReferencesModal';
import TicketingContext from '@totem/components/ticketing/listContainer/ticketingContainerContext';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { formatDate } from '@totem/utilities/common';

import '../events.css';

export interface Props {
  selectedTicketId?: string;
}

const TicketTable = ({ selectedTicketId }: Props) => {
  const { ticketData, loading, totalRecords, onTicketSelected } =
    useContext(TicketingContext);

  const { input, getFilterOptions, getFilterValues, handleTableChange } =
    useContext(InputContext);

  const callTicketAction = (ticket: TicketQueryResult) => {
    if (typeof onTicketSelected === 'function') {
      onTicketSelected(ticket.ticket.id, ticket);
    }
  };

  const getTicketClickAction = (ticket: TicketQueryResult) => {
    return (
      <Radio value={ticket.ticket.id} onClick={() => callTicketAction(ticket)}>
        <Link
          to={`/dashboard/ticket/${ticket.ticket.id}`}
          target="eventTicketDetails"
        >
          {ticket.ticket.number}
        </Link>
      </Radio>
    );
  };

  const columns: ColumnProps<TicketQueryResult>[] = [
    {
      title: 'Number',
      dataIndex: 'ticket.number',
      key: 'type',
      render: (_, ticket: TicketQueryResult) => getTicketClickAction(ticket),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.number === compB.ticket.number
          ? 0
          : compA.ticket.number > compB.ticket.number
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('type'),
      filters: getFilterOptions('type'),
    },
    {
      title: 'Category',
      dataIndex: 'ticket.category',
      key: 'category',
      render: (_, ticket: TicketQueryResult) => ticket.ticket.category,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.category === compB.ticket.category
          ? 0
          : compA.ticket.category > compB.ticket.category
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('category'),
      filters: getFilterOptions('category'),
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building',
      render: (_, ticket: TicketQueryResult) => ticket.building.name,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.building.name === compB.building.name
          ? 0
          : compA.building.name > compB.building.name
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('building'),
      filters: getFilterOptions('building'),
    },
    {
      title: 'Summary',
      dataIndex: 'ticket.summary',
      key: 'ticket.summary',
      render: (_, ticket: TicketQueryResult) => ticket.ticket.summary,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.ticket.summary === compB.ticket.summary
          ? 0
          : compA.ticket.summary > compB.ticket.summary
            ? 1
            : -1,
    },
    {
      title: 'Updated',
      dataIndex: 'ticket.updatedAt',
      key: 'ticket.updatedAt',
      render: (_, ticket: TicketQueryResult) =>
        formatDate(ticket.ticket.updatedAt),
      sorter: (compA, compB) =>
        compA.ticket.updatedAt === compB.ticket.updatedAt
          ? 0
          : compA.ticket.updatedAt > compB.ticket.updatedAt
            ? 1
            : -1,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
  ];

  const getTickets = () => {
    if (ticketData !== null) {
      if (ticketData.tickets !== null) {
        return ticketData.tickets;
      }
    }
    return [];
  };

  return (
    <Radio.Group
      value={
        typeof selectedTicketId !== 'undefined' && selectedTicketId !== null
          ? selectedTicketId
          : EMPTY_ID
      }
    >
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={getTickets()}
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          current: input.pagination.page,
          pageSize: input.pagination.pageSize,
          total: totalRecords,
          showSizeChanger: true,
        }}
        rowKey={(record) => record.ticket.id}
      />
    </Radio.Group>
  );
};

export default TicketTable;
